
import { useRef, useCallback, useEffect } from 'react';
import { UndoRedoManager } from '@/utils/undoRedoManager';
import { CursorManager } from '@/utils/cursorManager';

interface UseUndoRedoProps {
  contentEditableRef: React.RefObject<HTMLDivElement>;
  onContentChange: (content: string) => void;
  onSnapshotRestore?: (content: string, cursorPosition: number) => void;
}

export const useUndoRedo = ({ 
  contentEditableRef, 
  onContentChange, 
  onSnapshotRestore 
}: UseUndoRedoProps) => {
  const undoRedoManager = useRef(new UndoRedoManager());
  const isApplyingUndoRedo = useRef(false);

  const saveSnapshot = useCallback((content: string, force: boolean = false) => {
    if (!contentEditableRef.current || isApplyingUndoRedo.current) return;

    const cursorPosition = CursorManager.saveCursorPosition(contentEditableRef.current);
    
    if (force) {
      undoRedoManager.current.forceSnapshot(content, cursorPosition);
    } else {
      undoRedoManager.current.saveSnapshot(content, cursorPosition);
    }
  }, [contentEditableRef]);

  const saveSnapshotBeforeOperation = useCallback((content: string) => {
    if (!contentEditableRef.current || isApplyingUndoRedo.current) return;

    const cursorPosition = CursorManager.saveCursorPosition(contentEditableRef.current);
    undoRedoManager.current.saveSnapshotBeforeOperation(content, cursorPosition);
  }, [contentEditableRef]);

  const undo = useCallback(() => {
    if (!contentEditableRef.current || isApplyingUndoRedo.current) return false;

    const snapshot = undoRedoManager.current.undo();
    if (!snapshot) return false;

    isApplyingUndoRedo.current = true;
    console.log('UndoRedo: Applying undo');
    
    try {
      // Atualiza o conteúdo diretamente (sem formatação adicional)
      contentEditableRef.current.innerHTML = snapshot.content;
      
      // Restaura o cursor
      setTimeout(() => {
        if (contentEditableRef.current) {
          CursorManager.restoreCursorPosition(contentEditableRef.current, snapshot.cursorPosition);
        }
        isApplyingUndoRedo.current = false;
      }, 10);

      // Notifica o componente pai
      onContentChange(snapshot.content);
      onSnapshotRestore?.(snapshot.content, snapshot.cursorPosition);

      return true;
    } catch (error) {
      console.error('UndoRedo: Error applying undo:', error);
      isApplyingUndoRedo.current = false;
      return false;
    }
  }, [contentEditableRef, onContentChange, onSnapshotRestore]);

  const redo = useCallback(() => {
    if (!contentEditableRef.current || isApplyingUndoRedo.current) return false;

    const snapshot = undoRedoManager.current.redo();
    if (!snapshot) return false;

    isApplyingUndoRedo.current = true;
    console.log('UndoRedo: Applying redo');
    
    try {
      // Atualiza o conteúdo diretamente (sem formatação adicional)
      contentEditableRef.current.innerHTML = snapshot.content;
      
      // Restaura o cursor
      setTimeout(() => {
        if (contentEditableRef.current) {
          CursorManager.restoreCursorPosition(contentEditableRef.current, snapshot.cursorPosition);
        }
        isApplyingUndoRedo.current = false;
      }, 10);

      // Notifica o componente pai
      onContentChange(snapshot.content);
      onSnapshotRestore?.(snapshot.content, snapshot.cursorPosition);

      return true;
    } catch (error) {
      console.error('UndoRedo: Error applying redo:', error);
      isApplyingUndoRedo.current = false;
      return false;
    }
  }, [contentEditableRef, onContentChange, onSnapshotRestore]);

  // CORREÇÃO CIRÚRGICA: Escape imediato sem preventDefault
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const root = contentEditableRef.current as HTMLElement | null;
      const isInEditor = !!root && !!t && root.contains(t);
      const isPrompt = !!t?.closest('[data-prompt-input]');
      const isFormField = t && (t.tagName === 'TEXTAREA' || t.tagName === 'INPUT' || (t as any).isContentEditable);
      
      // 🔒 ESCAPE IMEDIATO: deixe campos fora do editor digitar em paz
      if (isPrompt || (isFormField && !isInEditor)) {
        return; // NADA de preventDefault antes desse return
      }

      if (isApplyingUndoRedo.current) {
        console.log('UndoRedo: Evento bloqueado - operação undo/redo em andamento');
        return;
      }

      // Daqui pra baixo, só trate atalhos quando estiver DENTRO do editor
      const ctrl = e.ctrlKey || e.metaKey;
      const shift = e.shiftKey;

      // trate APENAS combinações que você realmente usa
      if (isInEditor && ctrl && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (shift) redo(); else undo();
        return;
      }
      if (isInEditor && (ctrl && e.key.toLowerCase() === 'y')) {
        e.preventDefault();
        redo();
        return;
      }
      // qualquer outra tecla: não chame preventDefault
    };

    // **CORREÇÃO CIRÚRGICA**: Listener único bubble (não capture)
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('keydown', handleKeyDown, { passive: false });
    }
    
    return () => {
      if (typeof document !== 'undefined' && document.removeEventListener) {
        document.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [contentEditableRef, undo, redo]);

  const canUndo = useCallback(() => {
    return undoRedoManager.current.canUndo();
  }, []);

  const canRedo = useCallback(() => {
    return undoRedoManager.current.canRedo();
  }, []);

  const clearHistory = useCallback(() => {
    undoRedoManager.current.clear();
  }, []);

  return {
    saveSnapshot,
    saveSnapshotBeforeOperation,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory
  };
};
